import { makeCurrencyMask } from '@app/functions';
import { TaxCredit } from '@app/models/employee/payroll/tax-credit.model';
import { TAX_CREDIT_DEFAULT_PERSONAL_AMOUNTS, TaxCreditYear } from '@app/models/employee/payroll/types';
import { Column } from '@app/modules/table/classes/column.class';
import { CellDisplay } from '@app/modules/table/enums/cell-display.enum';
import { BadgeDisplay } from '@app/modules/table/interfaces/badge-display.interface';
import { LOCALIZED_DECIMAL, Locale } from '@app/types/translatable.type';
import { conformToMask } from 'text-mask-core/dist/textMaskCore';

// This column will default to $0.00 if no value is present
// However, there is also custom logic in here to determine if a column is irrelevant for the table. In which case it will instead display a "-"
// For example, some fields only appear for Quebec, therefore they should have a "-" for non-Quebec provinces
abstract class DefaultToZeroColumn extends Column {
    type: CellDisplay = CellDisplay.string;
    isQuebec?: boolean;
    /**
     * if shouldFallbackToJurisdictionDefault is true then the default value from TAX_CREDIT_DEFAULT_PERSONAL_AMOUNTS will be displayed in the column when the value is undefined
     * Any non-undefined value will still display here, including $0.00
     */
    shouldFallbackToJurisdictionDefault = false;

    constructor(private locale: Locale) {
        super();
    }

    getDisplayPropertyValue(taxCredit: TaxCredit): string {
        let value = taxCredit[this.property as keyof TaxCredit];

        if (value === undefined) {
            if (this.shouldFallbackToJurisdictionDefault) {
                const yearlyRates = TAX_CREDIT_DEFAULT_PERSONAL_AMOUNTS[parseInt(taxCredit.year) as TaxCreditYear];
                // In the case that we somehow receive a year from the BE that has not been populated in the front-end, nothing is displayed in the table (this should never happen)
                if (!yearlyRates) {
                    return '';
                }
                value = yearlyRates[taxCredit.jurisdiction];
            } else {
                value = `0${LOCALIZED_DECIMAL[this.locale]}00`;
            }
        }

        const currencyMask = makeCurrencyMask({ allowDecimal: true }, this.locale);
        const formattedValue = conformToMask(value.toString(), currencyMask, {}).conformedValue; // Format the value using the currency mask

        // If the isQuebec property isn't set, the field should always be returned as is
        if (this.isQuebec === undefined) {
            return formattedValue;
        }

        // Display a - for QC fields on non-QC provinces, and for non-QC fields on QC
        if (this.isQuebec === true) {
            return taxCredit.jurisdiction === 'QC' ? formattedValue : '-';
        } else {
            return taxCredit.jurisdiction !== 'QC' ? formattedValue : '-';
        }
    }
}

class JurisdictionColumn extends Column {
    title = 'employees.jobAndPay.taxCredits.fields.provincial.jurisdiction.label';
    property = 'jurisdiction';
    type: CellDisplay = CellDisplay.string;
    sortable = true;

    getDisplayPropertyValue(taxCredit: TaxCredit): string {
        return `provinces.${taxCredit.jurisdiction}`;
    }
}

class YearColumn extends Column {
    title = 'employees.jobAndPay.taxCredits.fields.calendarYear.label';
    property = 'year';
    type: CellDisplay = CellDisplay.string;
    sortable = true;
}

class ClaimAmountColumn extends DefaultToZeroColumn {
    title = 'employees.jobAndPay.taxCredits.fields.federal.claimAmount.label';
    property = 'claimAmount';
    isQuebec = false;
    shouldFallbackToJurisdictionDefault = true;
}

class FederalAdditionalDeductionsColumn extends DefaultToZeroColumn {
    title = 'employees.jobAndPay.taxCredits.fields.federal.federalAdditionalDeductions.label';
    property = 'federalAdditionalDeductions';
}

class DeductionForZoneColumn extends DefaultToZeroColumn {
    title = 'employees.jobAndPay.taxCredits.fields.federal.deductionForZone.label';
    property = 'deductionForZone';
}

class AnnualDeductionsColumn extends DefaultToZeroColumn {
    title = 'employees.jobAndPay.taxCredits.fields.federal.annualDeductions.label';
    property = 'annualDeductions';
}

class AlimonyPaymentsColumn extends DefaultToZeroColumn {
    title = 'employees.jobAndPay.taxCredits.fields.federal.alimonyPayments.label';
    property = 'alimonyPayments';
}

class OtherFederalColumn extends DefaultToZeroColumn {
    title = 'employees.jobAndPay.taxCredits.fields.federal.otherFederal.label';
    property = 'otherFederal';
}

class OtherProvincialColumn extends DefaultToZeroColumn {
    title = 'employees.jobAndPay.taxCredits.fields.federal.otherProvincial.label';
    property = 'otherProvincial';
}

class PersonalTaxColumn extends DefaultToZeroColumn {
    title = 'employees.jobAndPay.taxCredits.fields.provincial.personalTax.labelWithQC';
    property = 'personalTax';

    isQuebec = true;
    shouldFallbackToJurisdictionDefault = true;
}

class CareerExtensionColumn extends DefaultToZeroColumn {
    title = 'employees.jobAndPay.taxCredits.fields.provincial.careerExtension.labelWithQC';
    property = 'careerExtension';

    isQuebec = true;
}

class QuebecAdditionalDeductionsColumn extends DefaultToZeroColumn {
    title = 'employees.jobAndPay.taxCredits.fields.provincial.quebecAdditionalDeductions.labelWithQC';
    property = 'quebecAdditionalDeductions';

    isQuebec = true;
}

class DeductionsColumn extends DefaultToZeroColumn {
    title = 'employees.jobAndPay.taxCredits.fields.provincial.deductions.labelWithQC';
    property = 'deductions';

    isQuebec = true;
}

class DateAddedColumn extends Column {
    title = 'employees.jobAndPay.taxCredits.fields.dateAdded.label';
    property = 'createdAt';
    type = CellDisplay.date;
    sortable = true;
}

class StatusColumn extends Column {
    title = 'employees.jobAndPay.taxCredits.fields.status.label';
    property = 'state';
    type = CellDisplay.badge;
    sortable = true;

    getBadgeDisplay(taxCredit: TaxCredit): BadgeDisplay {
        return {
            type: taxCredit.state === 'active' ? 'informative' : 'default',
            label: `employees.jobAndPay.taxCredits.fields.status.${taxCredit.state}`,
        };
    }

    getCellText(): undefined {
        return undefined;
    }
}

export const FederalColumns = [
    YearColumn,
    ClaimAmountColumn,
    FederalAdditionalDeductionsColumn,
    DeductionForZoneColumn,
    AnnualDeductionsColumn,
    AlimonyPaymentsColumn,
    OtherFederalColumn,
    OtherProvincialColumn,
    DateAddedColumn,
    StatusColumn,
];
export const ProvincialColumns = [
    YearColumn,
    JurisdictionColumn,
    ClaimAmountColumn,
    PersonalTaxColumn,
    CareerExtensionColumn,
    QuebecAdditionalDeductionsColumn,
    DeductionsColumn,
    DateAddedColumn,
    StatusColumn,
];
